import { cn } from '@/lib/utils';

/*
 * A segmented switch: a radiogroup of bevel pills where the active one takes
 * the orange CTA treatment (game-type filter, server ranking score/time).
 */

export interface SegmentedOption<T extends string> {
  value: T;
  /** Already translated. */
  label: string;
}

interface SegmentedTabsProps<T extends string> {
  value: T;
  options: SegmentedOption<T>[];
  onChange: (value: T) => void;
  /** The group's accessible name. */
  label: string;
  className?: string;
}

export function SegmentedTabs<T extends string>({
  value,
  options,
  onChange,
  label,
  className,
}: SegmentedTabsProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn('flex flex-wrap items-center gap-1.5', className)}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(option.value)}
            className={cn(
              'bevel bg-btn text-titletext font-display px-3 py-1.5 text-[10px] uppercase tracking-wide outline-none transition-[filter] hover:brightness-112',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              active && 'bevel-orange bg-orange-cta text-orange-cta-tx',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
